import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, TextField, Typography, useIsMounted, useOverlayContext } from 'seagull';
import commonStyles from "../ChangeEmailAddress.module.scss";
import { ViewType, changeEmailErrorMessages, changeEmailSuccessMessages } from "../ChangeEmailAddressTypes";
import OTPInputSection from "../OTPInputSection";
import { useIoContext } from "../../../contexts";
import { useSendOTPCode } from "../../../hooks/useSendOTPCode/useSendOTPCode";
import { ServerError } from "../../../infra/ErrorResponse";
import { handleRequest } from "../../../infra/utils";
import { useStoreActions } from "../../../store/store-context";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const VerifyNewEmail = _ref => {
  let {
    handleClose,
    newUserEmail,
    handleSetView
  } = _ref;
  const {
    setAuthToken,
    api
  } = useStoreActions();
  const isMounted = useIsMounted();
  const {
    sendOTPCode
  } = useSendOTPCode();
  const overlayContext = useOverlayContext();
  const {
    cookies
  } = useIoContext();
  const [isLoading, setIsLoading] = useState(false);
  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    clearErrors
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      otp: ''
    }
  });
  const handleChangeEmailAndAuthenticate = async () => {
    const authToken = cookies.getAuthToken();
    await setAuthToken(authToken);
    overlayContext.showSuccessToast(changeEmailSuccessMessages.emailChanged, 'email-verified-successfully-toast');
    handleClose();
    return;
  };
  const handleError = error => {
    if (error.hasServerError(ServerError.InvalidOTP)) {
      setError('otp', {
        message: changeEmailErrorMessages.otpCodeIsInvalid
      });
      return;
    }
    if (error.hasServerError(ServerError.EmailAlreadyRegistered)) {
      setError('otp', {
        message: changeEmailErrorMessages.emailAlreadyRegistered
      });
      return;
    }
    if (error.isTooManyAttemptsError()) {
      handleSetView(ViewType.TooManyAttempts);
      return;
    }
    setError('otp', {
      message: changeEmailErrorMessages.somethingWentWrong
    });
  };
  const verifyEmailWithOTPCode = async _ref2 => {
    let {
      otp
    } = _ref2;
    setIsLoading(true);
    const changeEmailResult = await handleRequest(api.user.verifyEmailWithOTP({
      email: newUserEmail,
      otp: otp
    }));
    if (changeEmailResult.success) {
      await handleChangeEmailAndAuthenticate();
    } else {
      handleError(changeEmailResult.error);
    }
    if (isMounted()) {
      setIsLoading(false);
    }
  };
  const onSendOTPCode = async _ref3 => {
    let {
      showSuccessToast
    } = _ref3;
    await sendOTPCode({
      requestOTP: () => api.user.changeUserEmail({
        email: newUserEmail
      }),
      onTooManyRequests: () => handleSetView(ViewType.TooManyAttempts),
      showSuccessToast
    });
  };
  const resendCode = async () => {
    reset();
    await onSendOTPCode({
      showSuccessToast: true
    });
  };
  const otpValue = watch('otp');
  useEffect(() => {
    clearErrors('otp');
  }, [otpValue, clearErrors]);
  return /*#__PURE__*/_jsx("div", {
    "data-testid": "verify-email-address-view",
    children: /*#__PURE__*/_jsxs("form", {
      onSubmit: handleSubmit(verifyEmailWithOTPCode),
      children: [/*#__PURE__*/_jsxs("div", {
        className: commonStyles.content,
        children: [/*#__PURE__*/_jsxs(Typography, {
          variant: "paragraph1",
          "data-testid": "verify-email-address-info-text",
          children: ["To verify your new email address, we\u2019ll send you a confirmation email ", /*#__PURE__*/_jsx("br", {}), " containing a one-time code."]
        }), /*#__PURE__*/_jsx(TextField, {
          collapseHelperText: false,
          width: 360,
          "data-testid": "verify-email-address",
          type: "email",
          label: "New email address",
          value: newUserEmail,
          disabled: true
        }), /*#__PURE__*/_jsx(OTPInputSection, {
          isLoading: isLoading,
          control: control,
          resendCode: resendCode
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: commonStyles.footer,
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: handleClose,
          skin: "primaryOutlineOpaque",
          "data-testid": "verify-email-address-cancel-button",
          children: "Cancel"
        }), /*#__PURE__*/_jsx(Button, {
          type: "submit",
          "data-testid": "verify-otp-submit-button",
          loading: isLoading,
          children: "Verify code and update email"
        })]
      })]
    })
  });
};
export default VerifyNewEmail;